import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Palette, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FRAMES, BACKGROUND_SLOTS, applyTheme } from "@/lib/themes";
import ImageUploader from "@/components/ImageUploader";

const Section = ({ title, hint, children }) => (
  <section className="rounded-2xl bg-[#2B1B13] p-6 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
    <h2 className="font-serif text-xl font-medium text-[#F7F1E8]">{title}</h2>
    {hint && <p className="mt-1 text-xs text-[#F7F1E8]/68">{hint}</p>}
    <div className="mt-5">{children}</div>
  </section>
);

const PRICE_FIELDS = [
  { key: "consultation_30min", label: "Discuție scurtă la o cafea (30 min)" },
  { key: "consultation_60min", label: "Discuție pe îndelete (60 min)" },
  { key: "sponsor_mention", label: "Mențiune într-o însemnare" },
  { key: "sponsor_dedicated", label: "Însemnare dedicată" },
];

export const AdminPersonalizePage = () => {
  const [s, setS] = useState(null);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);

  useEffect(() => {
    api
      .get("/site-settings")
      .then((r) => {
        setS(r.data);
        setPrices(r.data.prices || {});
      })
      .catch((e) => toast.error(formatApiError(e, "Nu am putut încărca setările")));
  }, []);

  const set = (key, value) => setS((prev) => ({ ...prev, [key]: value }));

  const pickFrame = (key) => {
    set("frame_style", key);
    applyTheme({ ...s, frame_style: key });
  };
  const setBackground = (key, value) => {
    set(key, value);
    applyTheme({ ...s, [key]: value });
  };

  const save = async () => {
    setSaving(true);
    try {
      const bgPayload = Object.fromEntries(BACKGROUND_SLOTS.map(({ key }) => [key, s[key] ?? ""]));
      const { data } = await api.put("/admin/site-settings", {
        frame_style: s.frame_style,
        ...bgPayload,
        hero_kicker: s.hero_kicker ?? "",
        hero_title: s.hero_title ?? "",
        hero_subtitle: s.hero_subtitle ?? "",
        sponsor_image: s.sponsor_image ?? "",
        home_bottom_image: s.home_bottom_image ?? "",
        home_gallery_1: s.home_gallery_1 ?? "",
        home_gallery_2: s.home_gallery_2 ?? "",
        about_image_2: s.about_image_2 ?? "",
        about_image_3: s.about_image_3 ?? "",
        sponsor_image_2: s.sponsor_image_2 ?? "",
        sponsor_image_3: s.sponsor_image_3 ?? "",
      });
      setS(data);
      applyTheme(data);
      toast.success("Personalizarea a fost salvată!");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut salva"));
    } finally {
      setSaving(false);
    }
  };

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      const payload = {};
      PRICE_FIELDS.forEach(({ key }) => {
        const v = parseInt(prices[key], 10);
        if (v > 0) payload[key] = v;
      });
      const { data } = await api.post("/admin/prices", payload);
      setPrices(data.prices);
      toast.success("Prețurile au fost actualizate (și în Stripe)!");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut actualiza prețurile"));
    } finally {
      setSavingPrices(false);
    }
  };

  if (!s) {
    return <main className="mx-auto max-w-4xl px-6 py-16 text-center text-sm text-[#F7F1E8]/68">Se încarcă…</main>;
  }

  return (
    <main data-testid="admin-personalize-page" className="mx-auto max-w-4xl px-6 py-12">
      <Link to="/admin" data-testid="personalize-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>
      <h1 className="mt-6 flex items-center gap-3 font-serif text-4xl font-light text-[#F7F1E8]">
        <Palette className="h-7 w-7 text-[var(--accent)]" strokeWidth={1.4} /> Personalizare
      </h1>
      <p className="mt-2 text-sm text-[#F7F1E8]/78">Ajustează felul în care arată blogul. Fundalurile și ramele se văd instant, ca previzualizare.</p>

      <div className="mt-8 space-y-6">
        <Section title="Fundalurile cu cafea" hint="Fiecare pagină are propria fotografie. Dacă nu încarci nimic, rămâne fundalul original livrat cu blogul; „Șterge” revine la el.">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="background-options">
            {BACKGROUND_SLOTS.map(({ key, label, file }) => (
              <div key={key} data-testid={`background-slot-${key}`}>
                <ImageUploader testId={`${key}-upload`} label={label} aspect="aspect-[16/9]" value={s[key] || ""} onChange={(v) => setBackground(key, v)} allowRemove />
                {!s[key] && (
                  <div className="mt-2 flex items-center gap-3">
                    <img src={`/backgrounds/${file}`} alt="" className="h-10 w-16 rounded-md object-cover ring-1 ring-black/10" />
                    <span className="text-[11px] text-[#F7F1E8]/68">Fundalul original (activ acum)</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Rama fotografiilor" hint="Forma colțurilor la toate pozele de pe site.">
          <div className="flex flex-wrap gap-3" data-testid="frame-options">
            {Object.entries(FRAMES).map(([key, f]) => (
              <button
                key={key}
                data-testid={`frame-${key}`}
                onClick={() => pickFrame(key)}
                className={`flex items-center gap-3 rounded-xl border-2 px-5 py-3 transition-all ${
                  s.frame_style === key ? "border-[var(--accent)] bg-[var(--tint-pink)]" : "border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13]"
                }`}
              >
                <span className="h-8 w-10 bg-[var(--teal)]" style={{ borderRadius: f.radius }} />
                <span className="text-sm text-[#F7F1E8]">{f.label}</span>
              </button>
            ))}
          </div>
        </Section>

        <Section title="Textele de pe prima pagină" hint="Lasă un câmp gol ca să revii la textul original.">
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Rândul mic de deasupra titlului</label>
              <input data-testid="hero-kicker-input" value={s.hero_kicker || ""} onChange={(e) => set("hero_kicker", e.target.value)} maxLength={120}
                placeholder="Blogul Alexiei"
                className="mt-1.5 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907] px-4 py-2.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Titlul mare</label>
              <textarea data-testid="hero-title-input" value={s.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} maxLength={200} rows={2}
                placeholder="Lucrurile pe care nimeni nu ți le spune în față."
                className="mt-1.5 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907] px-4 py-2.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Paragraful de prezentare</label>
              <textarea data-testid="hero-subtitle-input" value={s.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} maxLength={600} rows={3}
                placeholder="Despre relații, oameni, bani și viața de adult — fără „depinde”, fără mănuși…"
                className="mt-1.5 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907] px-4 py-2.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]" />
            </div>
          </div>
        </Section>

        <Section title="Poze în locuri noi" hint="Opționale — dacă nu alegi nimic, locurile rămân ca înainte.">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <ImageUploader testId="sponsor-image-upload" label="Imagine pe pagina „Cumpără-mi o cafea”" aspect="aspect-[16/9]" value={s.sponsor_image || ""} onChange={(v) => set("sponsor_image", v)} allowRemove />
            <ImageUploader testId="home-bottom-image-upload" label="Imagine la finalul primei pagini" aspect="aspect-[16/9]" value={s.home_bottom_image || ""} onChange={(v) => set("home_bottom_image", v)} allowRemove />
          </div>

          <div className="mt-8 border-t border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pt-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[var(--accent)]">Poze decorative (opționale)</p>
            <p className="mt-1 text-[11px] text-[#F7F1E8]/68">
              Câte două pentru fiecare pagină — apar discret, doar dacă le încarci. Le poți schimba sau șterge oricând.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <ImageUploader testId="home-gallery-1-upload" label="Prima pagină — poza 1 (printre însemnări)" aspect="aspect-[4/3]" value={s.home_gallery_1 || ""} onChange={(v) => set("home_gallery_1", v)} allowRemove />
              <ImageUploader testId="home-gallery-2-upload" label="Prima pagină — poza 2 (printre însemnări)" aspect="aspect-[4/3]" value={s.home_gallery_2 || ""} onChange={(v) => set("home_gallery_2", v)} allowRemove />
              <ImageUploader testId="about-image-2-upload" label="Despre — poza 2 (sub portret)" aspect="aspect-[4/3]" value={s.about_image_2 || ""} onChange={(v) => set("about_image_2", v)} allowRemove />
              <ImageUploader testId="about-image-3-upload" label="Despre — poza 3 (sub portret)" aspect="aspect-[4/3]" value={s.about_image_3 || ""} onChange={(v) => set("about_image_3", v)} allowRemove />
              <ImageUploader testId="sponsor-image-2-upload" label="Cumpără-mi o cafea — poza 2 (între secțiuni)" aspect="aspect-[16/9]" value={s.sponsor_image_2 || ""} onChange={(v) => set("sponsor_image_2", v)} allowRemove />
              <ImageUploader testId="sponsor-image-3-upload" label="Cumpără-mi o cafea — poza 3 (la final)" aspect="aspect-[16/9]" value={s.sponsor_image_3 || ""} onChange={(v) => set("sponsor_image_3", v)} allowRemove />
            </div>
          </div>
        </Section>

        <button data-testid="personalize-save-btn" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-8 py-3 text-sm font-medium uppercase tracking-[0.15em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" strokeWidth={1.6} />}
          {saving ? "Se salvează…" : "Salvează personalizarea"}
        </button>

        <Section title="Prețurile" hint="În lei. Se actualizează automat și în Stripe — cumpărătorii plătesc noul preț imediat.">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {PRICE_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">{label}</label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input data-testid={`price-${key}`} type="number" min="1" max="10000" value={prices[key] ?? ""} onChange={(e) => setPrices((p) => ({ ...p, [key]: e.target.value }))}
                    className="w-28 rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907] px-4 py-2.5 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]" />
                  <span className="text-sm text-[#F7F1E8]/78">lei</span>
                </div>
              </div>
            ))}
          </div>
          <button data-testid="prices-save-btn" onClick={savePrices} disabled={savingPrices}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-6 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60">
            {savingPrices ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" strokeWidth={1.6} />}
            {savingPrices ? "Se actualizează…" : "Actualizează prețurile"}
          </button>
        </Section>
      </div>
    </main>
  );
};

export default AdminPersonalizePage;
